import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ClerkProvider } from '@clerk/react';
import { esMX } from '@clerk/localizations';
import * as Sentry from '@sentry/react';
import { App } from './App';
import { useUIStore } from './stores/ui-store';
import { clerkLight, clerkDark } from './shared/clerk-appearance';
import { DesktopAuthProvider } from './features/auth/desktop-auth';
import { initLanControl } from './features/auth/lan-control';
import { setDesktopToken } from './shared/api';

// LAN control: if the page loaded with a host-issued ?lanToken=… (from the
// operator's control QR), wire it as the desktop token for REST and bypass the
// Clerk/desktop auth providers — the device acts as the operator's org.
const LAN_CONTROL_TOKEN = initLanControl();
if (LAN_CONTROL_TOKEN) setDesktopToken(LAN_CONTROL_TOKEN);
const IS_LAN_CONTROL = !!LAN_CONTROL_TOKEN;

// True when running inside the Electron renderer (preload exposes electronAPI).
const IS_ELECTRON = typeof window !== 'undefined' && (window as { electronAPI?: { isElectron?: boolean } }).electronAPI?.isElectron === true;

// Sentry — renderer/browser error tracking.
// VITE_SENTRY_DSN is baked at build time. No-op when unset (dev / no DSN).
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (SENTRY_DSN) {
  const isElectron = IS_ELECTRON;
  Sentry.init({
    dsn: SENTRY_DSN,
    release: `proclaim-web@${import.meta.env.VITE_APP_VERSION ?? 'dev'}`,
    environment: import.meta.env.PROD
      ? (isElectron ? 'electron-renderer-prod' : 'web-prod')
      : (isElectron ? 'electron-renderer-dev' : 'web-dev'),
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    ignoreErrors: [
      // ResizeObserver loop limit exceeded — benign browser noise
      'ResizeObserver loop',
      // Network errors that fire during offline transitions
      'NetworkError',
      'Failed to fetch',
    ],
  });
}

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

// The custom desktop session layer (system-browser sign-in + handoff code → token)
// replaces Clerk-in-the-renderer for the PRODUCTION instance, which can't run on
// localhost (cross-site FAPI). The development instance (pk_test) authenticates
// the browser fine on localhost, so dev:electron keeps the normal Clerk flow.
const USE_DESKTOP_AUTH = IS_ELECTRON && PUBLISHABLE_KEY?.startsWith('pk_live') === true;

// LAN browser without a lanToken: the device opened the operator's local server
// URL directly (not via the control QR). pk_live can't initialise here because
// the LAN-IP origin isn't allowlisted in Clerk — useAuth would throw and the
// page would die. Show a friendly landing instead, asking the user to scan the
// QR from the desktop app.
const HOSTNAME = typeof window !== 'undefined' ? window.location.hostname : '';
const IS_LOCALHOST = HOSTNAME === 'localhost' || HOSTNAME === '127.0.0.1' || HOSTNAME === '::1';
const IS_PROD_DOMAIN = /iglesiaebenezer\.mx$/i.test(HOSTNAME);
const NEEDS_LAN_TOKEN =
  !IS_ELECTRON && !IS_LOCALHOST && !IS_PROD_DOMAIN && !LAN_CONTROL_TOKEN && PUBLISHABLE_KEY?.startsWith('pk_live') === true;

import './styles/globals.css';
import { initRangeSync } from './lib/range-sync';
// Bundled fonts — latin subset only (no cyrillic/greek/vietnamese)
import '@fontsource/inter/latin-300.css';
import '@fontsource/inter/latin-300-italic.css';
import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-400-italic.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-500-italic.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/inter/latin-600-italic.css';
import '@fontsource/inter/latin-700.css';
import '@fontsource/inter/latin-700-italic.css';
import '@fontsource/lora/latin-400.css';
import '@fontsource/lora/latin-400-italic.css';
import '@fontsource/lora/latin-500.css';
import '@fontsource/lora/latin-500-italic.css';
import '@fontsource/lora/latin-600.css';
import '@fontsource/lora/latin-600-italic.css';
import '@fontsource/lora/latin-700.css';
import '@fontsource/lora/latin-700-italic.css';
import '@fontsource/playfair-display/latin-400.css';
import '@fontsource/playfair-display/latin-400-italic.css';
import '@fontsource/playfair-display/latin-500.css';
import '@fontsource/playfair-display/latin-500-italic.css';
import '@fontsource/playfair-display/latin-600.css';
import '@fontsource/playfair-display/latin-600-italic.css';
import '@fontsource/playfair-display/latin-700.css';
import '@fontsource/playfair-display/latin-700-italic.css';
import '@fontsource/playfair-display/latin-800.css';
import '@fontsource/playfair-display/latin-800-italic.css';
import '@fontsource/playfair-display/latin-900.css';
import '@fontsource/playfair-display/latin-900-italic.css';
import '@fontsource/barlow-condensed/latin-300.css';
import '@fontsource/barlow-condensed/latin-300-italic.css';
import '@fontsource/barlow-condensed/latin-400.css';
import '@fontsource/barlow-condensed/latin-400-italic.css';
import '@fontsource/barlow-condensed/latin-500.css';
import '@fontsource/barlow-condensed/latin-500-italic.css';
import '@fontsource/barlow-condensed/latin-600.css';
import '@fontsource/barlow-condensed/latin-600-italic.css';
import '@fontsource/barlow-condensed/latin-700.css';
import '@fontsource/barlow-condensed/latin-700-italic.css';
import '@fontsource/roboto-slab/latin-300.css';
import '@fontsource/roboto-slab/latin-400.css';
import '@fontsource/roboto-slab/latin-500.css';
import '@fontsource/roboto-slab/latin-600.css';
import '@fontsource/roboto-slab/latin-700.css';

function ThemedClerkProvider({ children }: { children: React.ReactNode }) {
  const { darkMode } = useUIStore();
  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY!}
      localization={esMX}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      afterSignOutUrl="/sign-in"
      appearance={darkMode ? clerkDark : clerkLight}
    >
      {children}
    </ClerkProvider>
  );
}

const appTree = (
  <BrowserRouter>
    <App />
  </BrowserRouter>
);

initRangeSync();

// Field diagnostics. From DevTools (Ctrl+Shift+Alt+D in prod) run:
//   await __proclaimDiag()           → main-process DB/state report + sync cursors
//   __proclaimResync()               → clears pull/push cursors + reloads (full re-pull)
if (typeof window !== 'undefined') {
  (window as unknown as { __proclaimDiag: () => Promise<unknown> }).__proclaimDiag = async () => {
    const main = window.electronAPI?.diag ? await window.electronAPI.diag.report() : { note: 'not electron' };
    const cursors = {
      last_seq: localStorage.getItem('proclaim_last_seq'),
      last_pushed_seq: localStorage.getItem('proclaim_last_pushed_seq'),
      bible_local_count: localStorage.getItem('proclaim_bible_local_count'),
    };
    const out = { main, rendererCursors: cursors };
    console.log('[Proclaim diag]', out);
    return out;
  };
  (window as unknown as { __proclaimResync: () => void }).__proclaimResync = () => {
    localStorage.removeItem('proclaim_last_seq');
    localStorage.removeItem('proclaim_last_pushed_seq');
    console.log('[Proclaim] sync cursors cleared — reloading for full re-pull');
    location.reload();
  };
}

// ErrorBoundary catches uncaught React render errors and reports to Sentry.
// Falls back to a minimal recovery UI so users aren't stuck on a blank screen.
function FallbackUI({ error, resetError }: { error: unknown; resetError: () => void }) {
  return (
    <div style={{ padding: '2rem', fontFamily: 'sans-serif', textAlign: 'center' }}>
      <h1>Algo salió mal</h1>
      <p>El error fue reportado. Puedes intentar recargar.</p>
      {!import.meta.env.PROD && (
        <pre style={{ textAlign: 'left', overflow: 'auto', background: '#f3f3f3', padding: '1rem' }}>
          {error instanceof Error ? error.stack ?? error.message : String(error)}
        </pre>
      )}
      <button onClick={resetError} style={{ padding: '0.5rem 1rem', marginTop: '1rem' }}>
        Reintentar
      </button>
    </div>
  );
}

const guardedAppTree = (
  <Sentry.ErrorBoundary fallback={FallbackUI}>{appTree}</Sentry.ErrorBoundary>
);

// Landing shown when a LAN browser opens the Electron app's local URL directly,
// without the host-issued lanToken. ClerkProvider can't mount here (pk_live
// rejects the LAN-IP origin), so any useAuth call would crash the page.
function LanLandingPage() {
  return (
    <div style={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', background: '#0b0b0d', color: '#e7e7ea', padding: '1.5rem', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ maxWidth: 360, textAlign: 'center', background: '#17171a', border: '1px solid #2a2a2f', borderRadius: 24, padding: '2rem' }}>
        <h1 style={{ fontSize: '1.125rem', fontWeight: 700, margin: 0, marginBottom: 8 }}>Proclaim — Control remoto</h1>
        <p style={{ fontSize: '0.875rem', color: '#a1a1aa', lineHeight: 1.5, margin: 0 }}>
          Para controlar Proclaim desde este dispositivo, abre la aplicación de escritorio,
          ve a <strong style={{ color: '#e7e7ea' }}>En Vivo</strong> y escanea el código QR de la pestaña
          <strong style={{ color: '#e7e7ea' }}> &ldquo;Controlar&rdquo;</strong>.
        </p>
      </div>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {NEEDS_LAN_TOKEN ? (
      <LanLandingPage />
    ) : IS_LAN_CONTROL ? (
      guardedAppTree
    ) : USE_DESKTOP_AUTH ? (
      <DesktopAuthProvider>{guardedAppTree}</DesktopAuthProvider>
    ) : PUBLISHABLE_KEY ? (
      <ThemedClerkProvider>{guardedAppTree}</ThemedClerkProvider>
    ) : (
      guardedAppTree
    )}
  </React.StrictMode>,
);
